// MOCKED API LAYER — persists to browser localStorage.
// Same interface as the future axios-based backend client (returns { data }).
import axios from "axios";
import { MOCK_PORTFOLIO, MOCK_BLOG, MOCK_TEAM } from "@/mock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const NS = "serpens-mock-";

const read = (k, seed) => {
  try {
    const raw = localStorage.getItem(NS + k);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return seed;
};
const write = (k, v) => localStorage.setItem(NS + k, JSON.stringify(v));
const uid = () => (crypto?.randomUUID ? crypto.randomUUID() : String(Date.now() + Math.random()));
const now = () => new Date().toISOString();
const ok = (data, ms = 350) => new Promise((res) => setTimeout(() => res({ data }), ms));
const fail = (status, detail) =>
  new Promise((_, rej) => setTimeout(() => rej({ response: { status, data: { detail } } }), 250));
// Admin session = signed JWT issued by the backend (/api/admin/login). Password never lives in the frontend.
const tokenValid = (token) => {
  try {
    const payload = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    return payload.role === "admin" && payload.exp * 1000 > Date.now();
  } catch {
    return false;
  }
};
const guard = (key) => !!key && tokenValid(key);
const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const list = (k, seed) => read(k, seed);
const create = (k, seed, item) => {
  const items = list(k, seed);
  const rec = { ...item, id: uid(), created_at: now() };
  write(k, [rec, ...items]);
  return rec;
};
const update = (k, seed, id, patch) => {
  const items = list(k, seed).map((i) => (i.id === id ? { ...i, ...patch } : i));
  write(k, items);
  return items.find((i) => i.id === id);
};
const remove = (k, seed, id) => write(k, list(k, seed).filter((i) => i.id !== id));

export const api = {
  adminLogin: (password) => axios.post(`${API}/admin/login`, { password }),
  adminVerify: (token) => axios.get(`${API}/admin/verify`, { headers: { Authorization: `Bearer ${token}` } }),
  contact: (data) => {
    if (!data?.name || !data?.email || !data?.message) return fail(422, "Missing fields");
    create("contacts", [], data);
    return ok({ message: "Thanks! Our team will get back to you within 24 hours." });
  },
  newsletter: (data) => {
    const subs = list("newsletter", []);
    if (!subs.find((s) => s.email === data.email)) create("newsletter", [], data);
    return ok({ message: "You're subscribed! Watch your inbox for our next insight." });
  },
  apply: (data) => {
    create("careers", [], data);
    return ok({ message: "Application received! We will respond within 5 working days." });
  },
  chatSend: (data) => {
    create("chats", [], data);
    return ok({ ok: true });
  },
  blogList: () => ok(list("blog", MOCK_BLOG).map(({ content, ...rest }) => rest)),
  blogPost: (slug) => {
    const p = list("blog", MOCK_BLOG).find((b) => b.slug === slug);
    return p ? ok(p) : fail(404, "Not found");
  },
  portfolioList: () => ok(list("portfolio", MOCK_PORTFOLIO)),
  teamList: () => ok(list("team", MOCK_TEAM)),

  // admin
  portfolioCreate: (data, key) => (guard(key) ? ok(create("portfolio", MOCK_PORTFOLIO, data)) : fail(401, "Unauthorized")),
  portfolioUpdate: (id, data, key) => (guard(key) ? ok(update("portfolio", MOCK_PORTFOLIO, id, data)) : fail(401, "Unauthorized")),
  portfolioDelete: (id, key) => (guard(key) ? ok(remove("portfolio", MOCK_PORTFOLIO, id)) : fail(401, "Unauthorized")),
  blogCreate: (data, key) =>
    guard(key)
      ? ok(create("blog", MOCK_BLOG, { ...data, slug: data.slug || slugify(data.title), published_at: now() }))
      : fail(401, "Unauthorized"),
  blogUpdate: (id, data, key) => (guard(key) ? ok(update("blog", MOCK_BLOG, id, data)) : fail(401, "Unauthorized")),
  blogDelete: (id, key) => (guard(key) ? ok(remove("blog", MOCK_BLOG, id)) : fail(401, "Unauthorized")),
  teamCreate: (data, key) => {
    if (!guard(key)) return fail(401, "Unauthorized");
    const initials = data.name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
    return ok(create("team", MOCK_TEAM, { ...data, initials }));
  },
  teamUpdate: (id, data, key) => {
    if (!guard(key)) return fail(401, "Unauthorized");
    const initials = data.name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
    return ok(update("team", MOCK_TEAM, id, { ...data, initials }));
  },
  teamDelete: (id, key) => (guard(key) ? ok(remove("team", MOCK_TEAM, id)) : fail(401, "Unauthorized")),
  chatList: (key) => (guard(key) ? ok(list("chats", [])) : fail(401, "Unauthorized")),
  chatDelete: (id, key) => (guard(key) ? ok(remove("chats", [], id)) : fail(401, "Unauthorized")),
  contactList: (key) => (guard(key) ? ok(list("contacts", [])) : fail(401, "Unauthorized")),
  contactDelete: (id, key) => (guard(key) ? ok(remove("contacts", [], id)) : fail(401, "Unauthorized")),
  careersList: (key) => (guard(key) ? ok(list("careers", [])) : fail(401, "Unauthorized")),
  careersDelete: (id, key) => (guard(key) ? ok(remove("careers", [], id)) : fail(401, "Unauthorized")),
  uploadImage: (file, key) => {
    if (!guard(key)) return fail(401, "Unauthorized");
    return new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve({ data: { url: r.result, path: file.name } });
      r.onerror = reject;
      r.readAsDataURL(file);
    });
  },
};
